import {
	getIssueRemoteDataQuery,
	getIssueRemoteDataQueryWithProject,
	ISSUE_VIEW_REMOTE_DATA_QUERY,
	getRemoteDataQueryPrefix,
} from '@jira/issue__remote-data-query/src/getIssueRemoteDataQuery.tsx';
import type { ServerRemoteIssueLink } from '@jira/issue__shared-types/src/TypesServerPartialRemoteIssueLink.tsx';
import type { FailedRemoteLinkError } from '@jira/issue__shared-types/src/TypesFailedRemoteLinkError.tsx';
import { graphqlErrorHandling } from '@jira/issue__remote-data/src/graphqlErrorHandling.tsx';
import type { RemoteLinks } from '@jira/issue__shared-types/src/TypesServerRemoteLinks.tsx';
import type { JiraProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { getAriConfig, isAri } from '@jira/platform__utils-ari/src/index.tsx';
import { getClient } from './getClient.tsx';
import type { RemoteDataGraphQlResponse } from './RemoteDataGraphQlResponse.types.tsx';

type IssueKey = string;
type CloudId = string;

export const fetchIssueViewRemoteData = async (
	issueKey: IssueKey,
): Promise<RemoteDataGraphQlResponse> => {
	const variables = {
		issueKey,
	};

	const payload = {
		query: getIssueRemoteDataQuery(),
		variables,
		operationName: ISSUE_VIEW_REMOTE_DATA_QUERY,
	};

	const response = await getClient().query<RemoteDataGraphQlResponse>(payload);
	return response;
};

export const fetchIssueViewRemoteDataWithCloudId = async (
	issueKey: IssueKey,
	cloudId: CloudId,
): Promise<RemoteDataGraphQlResponse> => {
	const variables = {
		issueKey,
		cloudId,
	};

	const payload = {
		query: getIssueRemoteDataQueryWithProject(),
		variables,
		operationName: ISSUE_VIEW_REMOTE_DATA_QUERY,
	};

	const response = await getClient().query<RemoteDataGraphQlResponse>(payload);
	return response;
};

const LOG_LOCATION = 'issue.data-provider.source.issue-express.graphql';
const VIEW_ISSUE_ALIAS = 'bentoViewIssue';

export const RemoteJiraIssueLinkResultSuccessTypename = 'RemoteJiraIssueLinkResultSuccess' as const;

type RemoteJiraIssueLinkResultSuccess = {
	__typename: typeof RemoteJiraIssueLinkResultSuccessTypename;
	key: string;
	summary: string;
	statusId: number;
	statusName: string;
	statusCategoryId: number;
	isResolved: boolean;
	issueId: number;
	issueTypeName: string;
	issueTypeIconUrl: string;
	priorityName: string;
	priorityIconUrl: string;
	assigneeDisplayName: string;
	assigneeAvatarUrl: string;
};
export const RemoteJiraIssueLinkResultErrorTypename = 'RemoteJiraIssueLinkResultError' as const;
type RemoteJiraIssueLinkResultError = {
	__typename: typeof RemoteJiraIssueLinkResultErrorTypename;
	errorType?: FailedRemoteLinkError;
	repairLink?: string;
};
type RemoteJiraIssueLinkResult = RemoteJiraIssueLinkResultSuccess | RemoteJiraIssueLinkResultError;
type JiraIssueRemoteIssueLink = {
	id?: string;
	globalId?: string;
	relationship?: string;
	title?: string;
	href?: string;
	content?: RemoteJiraIssueLinkResult;
};
type JiraIssueRemoteIssueLinkNode = {
	node: JiraIssueRemoteIssueLink | null;
};
type JiraIssueRemoteIssueLinkConnection = {
	edges: JiraIssueRemoteIssueLinkNode[];
};

const getIdFromAri = (ari: string | undefined) => {
	if (typeof ari !== 'string') {
		return 0;
	}
	if (!isAri(ari)) {
		return 0;
	}
	try {
		const ariConfig = getAriConfig(ari);
		return parseInt(ariConfig.resourceId, 10);
	} catch (err) {
		// unable to parse the ari
		return 0;
	}
};

const getIssueIdFromGlobalId = (globalId: string | undefined) => {
	if (typeof globalId !== 'string') {
		return 0;
	}

	const match = globalId.match(/issueId=(\d+)/);
	if (match) {
		return parseInt(match[1], 10);
	}
	return 0;
};

const applicationDetails = {
	// hard-coded to `com.atlassian.jira` as this is constant in this context
	// and this doesn't make sense providing from the backend.
	// This value doesn't appear used and a separate change can trial removing it.
	applicationType: 'com.atlassian.jira',
	// hard-coded to `Jira` to avoid potential issues
	// and this doesn't make sense providing from the backend.
	// This value doesn't appear used and a separate change can trial removing it.
	applicationName: 'Jira',
};

export const transformRemoteIssueLinks: (
	edges: JiraIssueRemoteIssueLinkConnection['edges'] | null,
) => ServerRemoteIssueLink[] = (edges) => {
	if (!Array.isArray(edges)) {
		return [];
	}
	return edges
		.map((edge) => {
			if (!edge?.node?.content) {
				return undefined;
			}
			return transformRemoteLinkedIssue(edge.node);
		})
		.filter(Boolean);
};

export const transformRemoteLinkedIssue: (
	value: JiraIssueRemoteIssueLink,
) => ServerRemoteIssueLink = (value) => {
	if (value.content?.__typename === RemoteJiraIssueLinkResultErrorTypename) {
		return {
			preview: {
				id: getIdFromAri(value.id),
				// In the event of an error this value is not provided.
				issueId: getIssueIdFromGlobalId(value.globalId),
				globalId: value.globalId,
				href: value.href ?? '',
				...applicationDetails,
				relationship: value.relationship ?? '',
				title: value.title ?? '',
			},
			details: {
				error: value.content.errorType ?? 'REMOTE_LINK_MISSING',
				repairLink: value.content.repairLink
					? {
							href: value.content.repairLink,
						}
					: null,
			},
		};
	}

	return {
		preview: {
			id: getIdFromAri(value.id),
			issueId: value.content?.issueId ?? getIssueIdFromGlobalId(value.globalId),
			globalId: value.globalId,
			href: value.href ?? '',
			...applicationDetails,
			relationship: value.relationship ?? '',
			title: value.title ?? '',
		},
		details: {
			associatedIssue: {
				issueId: value.content?.issueId ?? 0,
				issueKey: {
					stringValue: value.content?.key ?? '',
				},
				summary: {
					textValue: value.content?.summary ?? '',
				},
				status: {
					statusCategoryId: value.content?.statusCategoryId ?? 0,
					name: value.content?.statusName ?? '',
					statusId: (value.content?.statusId ?? 0).toString(),
				},
				priority: {
					iconUrl: value.content?.priorityIconUrl ?? '',
					name: value.content?.priorityName ?? '',
					// `sequence` just to satisfy the types.
					sequence: undefined,
				},
				issueType: {
					iconUrl: value.content?.issueTypeIconUrl ?? '',
					name: value.content?.issueTypeName ?? '',
				},
				assignee:
					value.content?.assigneeDisplayName && value.content?.assigneeAvatarUrl
						? {
								userValue: {
									// `accountId` just to satisfy the types.
									accountId: '',
									displayName: value.content?.assigneeDisplayName ?? '',
									avatarUrl: value.content?.assigneeAvatarUrl ?? '',
								},
							}
						: null,
				isResolved: value.content?.isResolved ?? false,
			},
		},
	};
};

export const fetchIssueViewRemoteDataAndTransform = async (
	issueKey: IssueKey,
	cloudId: CloudId,
	storeCallback: (rawDataPromise: Promise<RemoteDataGraphQlResponse>) => void,
): Promise<{
	id: string;
	remoteLinks: RemoteLinks;
	projectType?: JiraProjectType;
}> => {
	const promise = fetchIssueViewRemoteDataWithCloudId(issueKey, cloudId);
	storeCallback(promise);
	const data = await promise;

	const responseWithIgnoredErrorsRemoved = graphqlErrorHandling.filterOutIgnoredErrors(
		data,
		graphqlErrorHandling.isIgnoredError,
	);

	const transformed = graphqlErrorHandling.getGraphQlData(
		LOG_LOCATION,
		responseWithIgnoredErrorsRemoved,
	);

	// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '`bentoViewIssue${string}`' can't be used to index type 'ViewIssueGraphqlResponse | ViewMultipleIssueGraphqlResponse'.
	const response = transformed[`${VIEW_ISSUE_ALIAS}${getRemoteDataQueryPrefix()}`];

	const edges = response?.remoteIssueLinks?.edges;

	const remoteLinkedIssues = transformRemoteIssueLinks(edges);

	return {
		id: response.issueId,
		remoteLinks: {
			...response.remoteLinks,
			remoteLinkedIssues,
		},
		projectType: data.data?.jira?.issueByKey?.projectField?.project?.projectType,
	};
};
